import React, { useState, useEffect, useMemo } from 'react';
import { useAssociation } from '../contexts/AssociationContext';
import { todayInTz, formatShortDateInTz } from '../utils/dateTime';
import ApiServiceTeacher from '../services/ApiServiceTeacher';
import { GLOBAL_UTILS, GLOBAL_STYLES } from '../styles/globalStyles';
import { mapQuranRecordFromBackend } from '../utils/quranRecordMapper';
import { QuranRecord } from '../types/api-routes';
import ChangePasswordModal from './shared/ChangePasswordModal';
import QuranTrackingSection from './shared/QuranTrackingSection';
import { FiRefreshCw } from 'react-icons/fi';
import MessageItem, { MessageItemData } from './shared/MessageItem';
import ExamScoresTable from './ExamScoresTable';
import SearchableSelect from './shared/SearchableSelect';

// Wrapper component pour l'icône de rafraîchissement (compatible TypeScript)
const RefreshIcon: React.FC<{ size?: number; style?: React.CSSProperties; animate?: boolean }> = ({ size = 16, style, animate = false }) => {
  const IconComponent = FiRefreshCw as any;
  return <IconComponent size={size} style={{ ...style, animation: animate ? 'spin 1s linear infinite' : 'none' }} />;
};


interface TeacherDashboardProps {
  onBackHome: () => void;
  onAuthError?: () => void;
  currentLanguage?: string;
  translations?: {
    ar: { [key: string]: string };
    fr: { [key: string]: string };
    en: { [key: string]: string };
  };
}

interface Student {
  id: number;
  student_name: string;
  level?: string;
  guardian_name?: string;
  phone?: string;
  average?: number;
  attendance?: number;
  status?: string;
  class_id?: number;
  class_name?: string;
  // Pour compatibilité avec le code existant
  studentName?: string;
  hizbCount?: number;
  averageGrade?: number;
  gender?: 'Female' | 'Male';
}

interface Message {
  id: number;
  title?: string;
  content?: string;
  created_date?: string;
  priority?: string;
  sender_name?: string;
  sender_type?: string;
  // Pour compatibilité avec le code existant
  from?: string;
  subject?: string;
  date?: string;
  sent_time?: string;
  isRead?: boolean;
  student_id?: number | null;
  student_name?: string | null;
  student_gender?: 'Male' | 'Female' | null;  // ⭐ NOUVEAU - Genre de l'étudiant
  student_label?: string | null;                // ⭐ NOUVEAU - Label calculé ("الطالب" ou "الطالبة")
  is_reply?: boolean; // Indique si c'est une réponse
  reply_to_message_id?: number | null; // ID du message auquel on répond
}

interface TeacherStats {
  totalStudents: number;
  monthlyRecords?: number;
  averageGrade?: number;
  unreadMessages?: number;
  // Pour compatibilité avec le code existant
  averageMemorization?: number;
  presentToday?: number;
  absentToday?: number;
  recentActivities?: Array<{
    studentName: string;
    activity: string;
    date: string;
    type: string;
  }>;
  progressDistribution?: {
    advanced: number;
    intermediate: number;
    beginner: number;
  };
}

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ onBackHome, onAuthError, currentLanguage = 'ar', translations }) => {
  const { association, getName, timezone } = useAssociation();
  const logoUrl = association?.logoUrl ?? '/images/logo.png';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTeacher, setCurrentTeacher] = useState<any>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [quranRecords, setQuranRecords] = useState<QuranRecord[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<TeacherStats | null>(null);
  const [todayHasSession, setTodayHasSession] = useState<boolean>(true); // false = aucune séance aujourd'hui
  const [activeSection, setActiveSection] = useState('teacherHome');
  const [messageTab, setMessageTab] = useState<'sent' | 'received'>('received');
  const [expandedMessageId, setExpandedMessageId] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showQuickReportModal, setShowQuickReportModal] = useState(false);
  const [showReplyMessageModal, setShowReplyMessageModal] = useState(false);
  const [messageToReplyTo, setMessageToReplyTo] = useState<Message | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [quickReport, setQuickReport] = useState({
    studentId: '',
    reportType: '',
    message: '',
    sendToAdmin: false, // Checkbox pour "الإدارة"
    subject: '' // Subject de la réponse
  });
  const [refreshing, setRefreshing] = useState<string | null>(null);
  
  // ✅ NOUVEAU : États pour la section "تقدم الطلاب"
  const [studentProgressTab, setStudentProgressTab] = useState<'examScores' | 'quranStats'>('examScores');
  const [testScores, setTestScores] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [showAddTestScoreForm, setShowAddTestScoreForm] = useState(false);
  const [newTestScore, setNewTestScore] = useState({
    student_id: '',
    subject_id: '',
    grade: '',
    date: todayInTz(timezone)
  });
  const [allowTeacherToAddExamPoints, setAllowTeacherToAddExamPoints] = useState<boolean>(false);

  const apiService = useMemo(() => ApiServiceTeacher.getInstance(), []);

  // Enregistrer le handler d'erreur d'authentification
  useEffect(() => {
    if (onAuthError && apiService && typeof apiService.setAuthErrorHandler === 'function') {
      apiService.setAuthErrorHandler(onAuthError);
    }
  }, [onAuthError, apiService]);

  // Fonction de traduction mémorisée pour éviter les re-renders inutiles
  const t = React.useCallback((key: string): string => {
    if (!translations) return key;
    const lang = (currentLanguage === 'ar' || currentLanguage === 'fr' || currentLanguage === 'en') ? currentLanguage : 'ar';
    return translations[lang]?.[key] || key;
  }, [translations, currentLanguage]);

  const isRTL = currentLanguage === 'ar';
  const subjectLocale = currentLanguage === 'fr' || currentLanguage === 'en' ? currentLanguage : 'ar';

  // Fonction pour traduire le type de rapport
  const translateReportType = (subject: string | null | undefined): string => {
    if (!subject) return '';
    const lowerSubject = subject.toLowerCase();
    
    // Détecter le type de rapport
    if (lowerSubject.includes('assignment') || lowerSubject.includes('واجب') || lowerSubject.includes('devoir')) {
      return t('reportTypeAssignment');
    }
    if (lowerSubject.includes('progress') || lowerSubject.includes('تقدم') || lowerSubject.includes('progrès')) {
      return t('reportTypeProgress');
    }
    if (lowerSubject.includes('achievement') || lowerSubject.includes('إنجاز') || lowerSubject.includes('réussite')) {
      return t('reportTypeAchievement');
    }
    if (lowerSubject.includes('concern') || lowerSubject.includes('ملاحظة') || lowerSubject.includes('note importante')) {
      return t('reportTypeConcern');
    }
    
    // Si c'est "Rapport assignment", "Rapport progress", etc.
    if (lowerSubject.includes('rapport')) {
      if (lowerSubject.includes('assignment')) return t('reportTypeAssignment');
      if (lowerSubject.includes('progress')) return t('reportTypeProgress');
      if (lowerSubject.includes('achievement')) return t('reportTypeAchievement');
      if (lowerSubject.includes('concern')) return t('reportTypeConcern');
    }
    
    return subject; // Retourner le sujet original si non reconnu
  };

  // Fonction pour traduire la priorité
  const translatePriority = (priority: string | null | undefined): string => {
    if (!priority) return t('priorityNormal');
    const lowerPriority = priority.toLowerCase();
    
    if (lowerPriority.includes('urgent') || lowerPriority.includes('عاجلة')) {
      return t('priorityUrgent');
    }
    if (lowerPriority.includes('high') || lowerPriority.includes('عالية')) {
      return t('priorityHigh');
    }
    return t('priorityNormal');
  };

  // NOTE: Toutes les fonctions liées à "متابعة القرآن" ont été déplacées dans QuranTrackingSection.tsx
  // Les fonctions suivantes sont maintenant dans QuranTrackingSection :
  // - loadSurahs, loadVerseCount
  // - formatQuranSelection, validateQuranSelection, renderQuranSelection
  // - calculateHizbFromSelection
  // - handleAddQuranRecord
  // - extractOriginalNoteText, wrapEvaluationText
  // - openAddQuranRecordModal, closeAddQuranRecordModal

  // Fonction pour obtenir le titre de l'expéditeur
  const getSenderTitle = (senderType: string | null | undefined): string => {
    if (!senderType) return '';
    const titleMap: { [key: string]: string } = {
      'teacher': t('senderTeacher'),
      'admin': t('senderAdmin'),
      'parent': t('senderParent'),
      'معلم': t('senderTeacher'),
      'مدير': t('senderAdmin'),
      'ولي أمر': t('senderParent')
    };
    return titleMap[senderType.toLowerCase()] || '';
  };

  // Calculer la distribution de progression localement à partir des données
  const calculateProgressDistribution = React.useCallback(() => {
    let advanced = 0;
    let intermediate = 0;
    let beginner = 0;

    students.forEach(student => {
      // Trouver le dernier enregistrement pour cet étudiant
      const studentRecords = quranRecords.filter(r => 
        r.studentId === student.id ||
        r.studentName === student.studentName
      );
      
      if (studentRecords.length > 0) {
        // Trier par date décroissante
        const sortedRecords = studentRecords.sort((a, b) => {
          const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
          const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
          return dateB - dateA;
        });
        
        const latestRecord = sortedRecords[0];
        const hizbValue = latestRecord.totalHizb;
        let totalHizb = 0;
        
        if (typeof hizbValue === 'string') {
          totalHizb = parseFloat(hizbValue) || 0;
        } else if (typeof hizbValue === 'number') {
          totalHizb = hizbValue;
        }
        
        // Classer selon le nombre de hizb
        if (totalHizb >= 15) {
          advanced++;
        } else if (totalHizb >= 5) {
          intermediate++;
        } else {
          beginner++;
        }
      } else {
        beginner++;
      }
    });
    
    return { advanced, intermediate, beginner };
  }, [students, quranRecords]);

  // Fonction pour dédupliquer les messages par ID
  const deduplicateMessages = (messages: Message[]): Message[] => {
    const seen = new Set<number>();
    return messages.filter(message => {
      if (message.id && seen.has(message.id)) {
        return false;
      }
      if (message.id) {
        seen.add(message.id);
      }
      return true;
    });
  };

  const loadDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // Charger les données du profil enseignant
      const profileResponse = await apiService.getTeacherProfile();
      if (profileResponse.success) {
        setCurrentTeacher(profileResponse.data);
      }

      // Déclarer les variables pour les données mappées
      let mappedStudents: Student[] = [];
      let mappedRecords: QuranRecord[] = [];

      // Charger les étudiants assignés
      const studentsResponse = await apiService.getTeacherStudents();
      if (studentsResponse.success) {
        console.log('📊 TEACHER DASHBOARD - Étudiants reçus:', studentsResponse.data);
        // Mapper les données du backend vers le format frontend avec nettoyage du texte arabe
        mappedStudents = (studentsResponse.data || []).map((student: any) => {
          const cleanedStudent = {
            ...student,
            id: student.id,
            studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            level: GLOBAL_UTILS.cleanText(student.level || ''),
            averageGrade: student.average || student.averageGrade || 0,
            average: student.average || student.averageGrade || 0,
            hizbCount: 0, // Ce champ n'est pas retourné par le backend, sera calculé si nécessaire
            // S'assurer que le genre est bien mappé (le spread ...student devrait déjà l'inclure, mais on le force explicitement)
            gender: student.gender || student.Gender || undefined
          };
          console.log('📊 TEACHER DASHBOARD - Étudiant mappé:', {
            id: cleanedStudent.id,
            name: cleanedStudent.student_name,
            gender: cleanedStudent.gender,
            rawGender: student.gender
          });
          return cleanedStudent;
        });
        console.log('📊 TEACHER DASHBOARD - Nombre d\'étudiants mappés:', mappedStudents.length);
        setStudents(mappedStudents);
      } else {
        console.error('❌ TEACHER DASHBOARD - Erreur lors du chargement des étudiants:', studentsResponse.error);
        setStudents([]);
      }

      // Charger les enregistrements Coran
      const quranResponse = await apiService.getTeacherQuranRecords();
      
      // Récupérer les sessions suivantes pour cet enseignant
      let nextSessionsMap = new Map();
      const currentTeacher = apiService.getCurrentUser();
      const teacherId = currentTeacher?.id || currentTeacher?.user_id || currentTeacher?.teacherId;
      
      if (teacherId) {
        try {
          const nextSessionsResponse = await apiService.getNextSessionsByTeacher(teacherId);
          if (nextSessionsResponse.success && nextSessionsResponse.data) {
            nextSessionsResponse.data.forEach((nextSession: any) => {
              const studentId = nextSession.studentId || nextSession.student_id;
              if (studentId) {
                nextSessionsMap.set(studentId, nextSession);
              }
            });
          }
        } catch (nextSessionError) {
          console.warn('⚠️ TEACHER DASHBOARD - Erreur lors de la récupération des sessions suivantes:', nextSessionError);
        }
      }
      
      if (quranResponse.success) {
        // Utiliser la fonction de mapping centralisée
        mappedRecords = (quranResponse.data || []).map((record: any) => {
          const studentId = record.student_id || record.studentId;
          const nextSession = nextSessionsMap.get(studentId) || null;
          return mapQuranRecordFromBackend(record, nextSession);
        });
        setQuranRecords(mappedRecords);
        
        // Log pour déboguer les dates des enregistrements
        // ✅ Jour « aujourd'hui » dans le fuseau de l'ASSOCIATION
        const todayStr = todayInTz(timezone);
        const recordsToday = mappedRecords.filter((record: any) => {
          const recordDate = record.modifiedDate || record.createdDate;
          if (!recordDate) return false;
          const recordDateStr = new Date(recordDate).toISOString().split('T')[0];
          return recordDateStr === todayStr;
        });
        console.log('🔍 TEACHER DASHBOARD - Enregistrements pour aujourd\'hui:', {
          todayStr: todayStr,
          totalRecords: mappedRecords.length,
          recordsToday: recordsToday.length,
          recordsTodayDetails: recordsToday.map((r: any) => ({
            id: r.id,
            studentId: r.studentId,
            modifiedDate: r.modifiedDate,
            createdDate: r.createdDate,
            evalNewHifz: r.evalNewHifz,
            evalOldHifzMurajah: r.evalOldHifzMurajah,
            evalNewHifzMurajah: r.evalNewHifzMurajah
          })),
          recentRecords: mappedRecords.slice(0, 5).map((r: any) => ({
            id: r.id,
            modifiedDate: r.modifiedDate,
            createdDate: r.createdDate,
            dateStr: r.modifiedDate ? new Date(r.modifiedDate).toISOString().split('T')[0] : null
          }))
        });
      }

      // Charger les messages
      const receiveLogStart = {
        action: 'receive_messages',
        component: 'TeacherDashboard',
        timestamp: new Date().toISOString(),
        route: '/api/teacher/messages',
        method: 'GET'
      };
      console.log('📥 TEACHER DASHBOARD - loadDashboardData - Début réception:', JSON.stringify(receiveLogStart, null, 2));
      
      const messagesResponse = await apiService.getTeacherMessages();
      
      const receiveLogResponse = {
        action: 'receive_messages',
        component: 'TeacherDashboard',
        timestamp: new Date().toISOString(),
        response: {
          success: messagesResponse?.success || false,
          messages_count: messagesResponse?.data ? (Array.isArray(messagesResponse.data) ? messagesResponse.data.length : 0) : 0,
          error: messagesResponse?.error || null
        }
      };
      console.log('📥 TEACHER DASHBOARD - loadDashboardData - Réponse:', JSON.stringify(receiveLogResponse, null, 2));
      
      if (messagesResponse.success) {
        // Fonction helper pour extraire le nom de l'étudiant depuis le contenu
        const extractStudentName = (content: string): { studentName: string | null; cleanContent: string } => {
          if (!content) return { studentName: null, cleanContent: content || '' };
          
          let cleanContent = GLOBAL_UTILS.cleanText(content);
          let studentName = null;
          
          // Chercher le pattern avec les différentes formes inclusives
          const studentLabelPatterns = [
            /الطالب\/ـة:\s*([^\n]+)/i,       // Arabe: الطالب/ـة
            /الطالب\/الطالبة:\s*([^\n]+)/i,  // Arabe: الطالب/الطالبة (ancien format)
            /الطالب\(ة\):\s*([^\n]+)/i,      // Arabe: الطالب(ة)
            /الطالب:\s*([^\n]+)/i,           // Arabe: الطالب (ancien format)
            /Student\(s\):\s*([^\n]+)/i,     // Anglais: Student(s)
            /Student:\s*([^\n]+)/i,          // Anglais: Student (ancien format)
            /Étudiant\(e\):\s*([^\n]+)/i,    // Français: Étudiant(e)
            /Étudiant:\s*([^\n]+)/i          // Français: Étudiant (ancien format)
          ];
          
          for (const pattern of studentLabelPatterns) {
            const match = cleanContent.match(pattern);
            if (match && match[1]) {
              studentName = match[1].trim();
              // Retirer le nom de l'étudiant du contenu pour l'affichage
              cleanContent = cleanContent.replace(pattern, '').trim();
              // Retirer les sauts de ligne multiples au début
              cleanContent = cleanContent.replace(/^\n+/, '').trim();
              break;
            }
          }
          
          return { studentName, cleanContent };
        };
        
        // Mapper les données du backend vers le format frontend
        // student_id est maintenant inclus dans la réponse de l'API
        const mappedMessages = (messagesResponse.data || []).map((message: any) => {
          const { studentName, cleanContent } = extractStudentName(message.content || '');
          
          // Utiliser student_id pour trouver le nom de l'étudiant dans la liste des étudiants
          // Utiliser mappedStudents qui vient d'être chargé, pas l'état students
          let finalStudentName = null;
          if (message.student_id) {
            const student = mappedStudents.find(s => s.id === message.student_id);
            finalStudentName = student ? student.student_name : null;
          }
          
          // Si pas trouvé via student_id, utiliser student_name de l'API
          if (!finalStudentName && message.student_name) {
            finalStudentName = message.student_name;
          }
          
          // Sinon, utiliser le nom extrait depuis le contenu
          if (!finalStudentName) {
            finalStudentName = studentName;
          }
          
          return {
            ...message,
            id: message.id,
            from: message.sender_name || message.from,
            subject: message.title || message.subject,
            date: message.created_date || message.date,
            content: cleanContent,
            isRead: false, // Le backend ne retourne pas ce champ dans la nouvelle structure
            // student_id est maintenant inclus dans la réponse de l'API (peut être null)
            student_id: message.student_id || null,
            // Utiliser le nom trouvé via student_id en priorité, sinon depuis l'API, sinon depuis le contenu
            student_name: finalStudentName,
            // Mapper reply_to_message_id depuis l'API
            is_reply: message.reply_to_message_id !== null && message.reply_to_message_id !== undefined,
            reply_to_message_id: message.reply_to_message_id || null
          };
        });
        // Dédupliquer les messages par ID
        const uniqueMessages = deduplicateMessages(mappedMessages);
        setMessages(uniqueMessages);
        
        const receiveLogSuccess = {
          action: 'receive_messages',
          component: 'TeacherDashboard',
          timestamp: new Date().toISOString(),
          result: {
            messages_before_dedup: mappedMessages.length,
            messages_after_dedup: uniqueMessages.length,
            first_message_id: uniqueMessages[0]?.id || null
          }
        };
        console.log('✅ TEACHER DASHBOARD - loadDashboardData - Messages chargés:', JSON.stringify(receiveLogSuccess, null, 2));
      }

      // Calculer la distribution de progression localement avec les données chargées
      const calculateDistributionFromData = (studentsData: Student[], recordsData: QuranRecord[]) => {
        let advanced = 0;
        let intermediate = 0;
        let beginner = 0;

        studentsData.forEach(student => {
          // Trouver le dernier enregistrement pour cet étudiant
          const studentRecords = recordsData.filter(r => 
            r.studentId === student.id ||
            r.studentName === student.studentName
          );
          
          if (studentRecords.length > 0) {
            // Trier par date décroissante
            const sortedRecords = studentRecords.sort((a, b) => {
              const dateA = new Date(a.modifiedDate || a.createdDate || 0).getTime();
              const dateB = new Date(b.modifiedDate || b.createdDate || 0).getTime();
              return dateB - dateA;
            });
            
            const latestRecord = sortedRecords[0];
            const hizbValue = latestRecord.totalHizb;
            let totalHizb = 0;
            
            if (typeof hizbValue === 'string') {
              totalHizb = parseFloat(hizbValue) || 0;
            } else if (typeof hizbValue === 'number') {
              totalHizb = hizbValue;
            }
            
            // Classer selon le nombre de hizb
            if (totalHizb >= 15) {
              advanced++;
            } else if (totalHizb >= 5) {
              intermediate++;
            } else {
              beginner++;
            }
          } else {
            // Si aucun enregistrement, considérer comme débutant
            beginner++;
          }
        });

        return { advanced, intermediate, beginner };
      };

      // ✅ Statistiques enseignant chargées AVANT l'attendance : la réponse porte l'année
      // scolaire de référence (année courante du système) → périmètre élèves cohérent.
      const statsResponse = await apiService.getTeacherStats();
      const schoolYearId = statsResponse.success ? (statsResponse.data?.schoolYear?.id ?? null) : null;

      // Charger les statistiques de présence/absence pour aujourd'hui
      const today = new Date();
      // ✅ Jour « aujourd'hui » dans le fuseau de l'ASSOCIATION (et non celui du navigateur)
      const todayStr = todayInTz(timezone); // Format YYYY-MM-DD
      
      console.log('🔍 TEACHER DASHBOARD - loadDashboardData - Date du jour:', {
        today: today,
        todayISO: today.toISOString(),
        todayStr: todayStr,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
      const attendanceResponse = await apiService.getAttendance({
        startDate: todayStr,
        endDate: todayStr,
        // ✅ Périmètre = élèves INSCRITS pour l'année de référence
        school_year_id: schoolYearId ?? undefined
      });

      console.log('🔍 TEACHER DASHBOARD - loadDashboardData - Réponse attendance:', {
        success: attendanceResponse.success,
        error: attendanceResponse.error,
        data: attendanceResponse.data,
        present: attendanceResponse.data?.present,
        absent: attendanceResponse.data?.absent,
        total: attendanceResponse.data?.total,
        sessionDay: attendanceResponse.data?.sessionDay
      });

      // Calculer la distribution de progression localement avec les données chargées
      const progressDistribution = calculateDistributionFromData(mappedStudents, mappedRecords);

      // ✅ Jour sans séance : l'API signalerait tous les élèves « absents » → on affiche 0/0
      const sessionDay = attendanceResponse.success ? (attendanceResponse.data?.sessionDay !== false) : true;
      setTodayHasSession(sessionDay);

      // Construire l'objet stats avec les données d'attendance
      const presentTodayValue = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.present ?? 0) : 0;
      const absentTodayValue = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.absent ?? 0) : 0;
      
      const statsData: TeacherStats = {
        totalStudents: mappedStudents.length,
        progressDistribution,
        ...(statsResponse.success ? statsResponse.data : {}),
        presentToday: presentTodayValue,
        absentToday: absentTodayValue
      };

      console.log('✅ TEACHER DASHBOARD - loadDashboardData - Stats construites:', {
        ...statsData,
        studentsCount: mappedStudents.length,
        recordsCount: mappedRecords.length,
        attendanceResponseSuccess: attendanceResponse.success,
        attendanceResponseError: attendanceResponse.error,
        attendanceData: attendanceResponse.data,
        presentTodayValue: presentTodayValue,
        absentTodayValue: absentTodayValue
      });

      setStats(statsData);

    } catch (error) {
      setError(t('loadingDataError'));
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiService, t, timezone]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // ✅ NOUVEAU : Charger le paramètre allowTeacherToAddExamPoints
  useEffect(() => {
    const loadParameters = async () => {
      try {
        const response = await apiService.getParameters();
        if (response.success && Array.isArray(response.data)) {
          const param = response.data.find((p: any) => p.name === 'allowTeacherToAddExamPoints');
          if (param) {
            setAllowTeacherToAddExamPoints(param.value === 'TRUE' || param.value === 'true');
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    };
    
    loadParameters();
  }, [apiService]);

  // ✅ NOUVEAU : Charger les notes et les matières quand on entre dans la section studentProgress
  useEffect(() => {
    if (activeSection === 'studentProgress') {
      const loadExamData = async () => {
        try {
          // Charger les notes
          const gradesResponse = await apiService.getGrades(subjectLocale);
          if (gradesResponse.success && Array.isArray(gradesResponse.data)) {
            setTestScores(gradesResponse.data);
          }
          
          // Charger les matières
          const subjectsResponse = await apiService.getSubjects(subjectLocale);
          if (subjectsResponse.success && Array.isArray(subjectsResponse.data)) {
            setSubjects(subjectsResponse.data);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des données d\'examen:', error);
        }
      };
      
      loadExamData();
    }
  }, [activeSection, apiService, subjectLocale]);

  // Recalculer les statistiques quand les données changent
  useEffect(() => {
    if ((students.length > 0 || quranRecords.length > 0) && !loading) {
      const progressDistribution = calculateProgressDistribution();
      console.log('🔄 TEACHER DASHBOARD - Recalcul des statistiques:', {
        progressDistribution,
        studentsCount: students.length,
        recordsCount: quranRecords.length
      });
      setStats(prevStats => ({
        ...prevStats,
        totalStudents: students.length,
        progressDistribution
      }));
    }
  }, [students, quranRecords, calculateProgressDistribution, loading]);

  // Gérer la largeur de la fenêtre pour la responsivité
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showTeacherSection = (section: string) => {
    setActiveSection(section);
  };

  const handleLogout = () => {
    apiService.logout();
    onBackHome();
  };

  // ✅ NOUVEAU : Fonctions pour gérer les نقط الإختبارات (notes d'examen)
  const handleAddTestScore = async () => {
    if (!allowTeacherToAddExamPoints) {
      alert(t('noPermissionAdd'));
      return;
    }

    // Validation
    if (!newTestScore.student_id || !newTestScore.subject_id || !newTestScore.grade || !newTestScore.date) {
      alert(t('fillRequiredFields'));
      return;
    }

    const grade = parseFloat(newTestScore.grade);
    if (isNaN(grade) || grade < 0 || grade > 20) {
      alert(t('invalidGrade'));
      return;
    }

    try {
      const testScoreData = {
        student_id: parseInt(newTestScore.student_id),
        subject_id: parseInt(newTestScore.subject_id),
        grade: grade,
        date: newTestScore.date
      };

      console.log('📤 TEACHER - handleAddTestScore - Envoi des données:', testScoreData);
      const response = await apiService.createGrade(testScoreData, subjectLocale);
      console.log('📥 TEACHER - handleAddTestScore - Réponse:', response);

      if (response.success) {
        // Recharger les notes depuis l'API
        const testScoresData = await apiService.getGrades(subjectLocale);
        if (testScoresData.success && Array.isArray(testScoresData.data)) {
          setTestScores(testScoresData.data);
        }

        // Réinitialiser le formulaire
        setNewTestScore({
          student_id: '',
          subject_id: '',
          grade: '',
          date: todayInTz(timezone)
        });
        setShowAddTestScoreForm(false);
        alert(t('examScoreAdded'));
      } else {
        alert(response.error || t('examScoreAddFailed'));
      }
    } catch (error) {
      console.error('خطأ في إضافة نقطة الاختبار:', error);
      alert(t('examScoreAddError'));
    }
  };

  const handleDeleteTestScore = async (gradeId: number) => {
    if (!allowTeacherToAddExamPoints) {
      alert(t('noPermissionDelete'));
      return;
    }

    if (!window.confirm(t('confirmDeleteScore'))) {
      return;
    }

    try {
      const response = await apiService.deleteGrade(gradeId);

      if (response.success) {
        // Recharger les notes depuis l'API
        const testScoresData = await apiService.getGrades(subjectLocale);
        if (testScoresData.success && Array.isArray(testScoresData.data)) {
          setTestScores(testScoresData.data);
        }
        alert(t('examScoreDeleted'));
      } else {
        alert(response.error || t('examScoreDeleteFailed'));
      }
    } catch (error) {
      console.error('خطأ في حذف نقطة الاختبار:', error);
      alert(t('examScoreDeleteError'));
    }
  };

  // Styles de navigation alignés sur لوحة المدير
  const getNavButtonStyle = (key: 'teacherHome' | 'quranTracking' | 'studentProgress' | 'teacherMessages', isActive: boolean) => {
    const palettes: Record<string, { activeBg: string; activeColor: string; borderColor: string }> = {
      teacherHome: { activeBg: '#e8f5e8', activeColor: '#2e7d32', borderColor: '#4caf50' }, // vert
      quranTracking: { activeBg: '#f3e5f5', activeColor: '#7b1fa2', borderColor: '#9c27b0' }, // violet
      studentProgress: { activeBg: '#e3f2fd', activeColor: '#1565c0', borderColor: '#2196f3' }, // bleu
      teacherMessages: { activeBg: '#fff3e0', activeColor: '#f57c00', borderColor: '#ff9800' } // orange
    };
    const p = palettes[key];
    if (isActive) {
      return {
        backgroundColor: p.activeBg,
        color: p.activeColor,
        borderBottom: `2px solid ${p.borderColor}`
      } as React.CSSProperties;
    }
    return {
      backgroundColor: 'transparent',
      color: '#666666',
      borderBottom: '2px solid transparent'
    } as React.CSSProperties;
  };

  // NOTE: openAddQuranRecordModal et closeAddQuranRecordModal ont été déplacées dans QuranTrackingSection.tsx

  const refreshStats = React.useCallback(async () => {
    setRefreshing('stats');
    try {
      // Forcer le rechargement complet des étudiants lors du refresh
      let updatedStudents = students;
      try {
        const studentsResponse = await apiService.getTeacherStudents(true);
        if (studentsResponse.success && studentsResponse.data) {
          const mappedStudents = (studentsResponse.data || []).map((student: any) => ({
            ...student,
            id: student.id,
            studentName: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            student_name: GLOBAL_UTILS.cleanText(student.student_name || student.studentName || `طالب ${student.id}`),
            level: GLOBAL_UTILS.cleanText(student.level || ''),
            averageGrade: student.average || student.averageGrade || 0,
            average: student.average || student.averageGrade || 0,
            hizbCount: 0
          }));
          setStudents(mappedStudents);
          updatedStudents = mappedStudents;
          console.log('🔄 TEACHER DASHBOARD - Étudiants rechargés lors du refresh:', mappedStudents.length);
        }
      } catch (error) {
        console.warn('⚠️ TEACHER DASHBOARD - Erreur lors du rechargement des étudiants:', error);
      }
      
      // Charger les statistiques de présence/absence pour aujourd'hui
      // ✅ Jour « aujourd'hui » dans le fuseau de l'ASSOCIATION (et non celui du navigateur)
      const today = new Date();
      const todayStr = todayInTz(timezone); // Format: "YYYY-MM-DD"
      
      console.log('🔍 TEACHER DASHBOARD - refreshQuranRecords - Date du jour:', {
        today: today,
        todayISO: today.toISOString(),
        todayStr: todayStr,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
      // ✅ Statistiques AVANT l'attendance : l'année scolaire de référence vient de /teacher/stats
      const statsResponse = await apiService.getTeacherStats();
      const schoolYearId = statsResponse.success ? (statsResponse.data?.schoolYear?.id ?? null) : null;

      const attendanceResponse = await apiService.getAttendance({
        startDate: todayStr,
        endDate: todayStr,
        // ✅ Périmètre = élèves INSCRITS pour l'année de référence
        school_year_id: schoolYearId ?? undefined
      });

      console.log('🔍 TEACHER DASHBOARD - refreshQuranRecords - Réponse attendance:', {
        success: attendanceResponse.success,
        error: attendanceResponse.error,
        data: attendanceResponse.data,
        present: attendanceResponse.data?.present,
        absent: attendanceResponse.data?.absent,
        total: attendanceResponse.data?.total,
        sessionDay: attendanceResponse.data?.sessionDay
      });

      // Calculer la distribution de progression localement
      const progressDistribution = calculateProgressDistribution();

      // ✅ Jour sans séance → 0/0 + mention « Aucune séance »
      const sessionDay = attendanceResponse.success ? (attendanceResponse.data?.sessionDay !== false) : true;
      setTodayHasSession(sessionDay);

      // Construire l'objet stats avec les données d'attendance
      const presentTodayValue = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.present ?? 0) : 0;
      const absentTodayValue = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.absent ?? 0) : 0;
      
      const statsData: TeacherStats = {
        totalStudents: updatedStudents.length,
        progressDistribution,
        ...(statsResponse.success ? statsResponse.data : {}),
        presentToday: presentTodayValue,
        absentToday: absentTodayValue
      };

      setStats(statsData);
      console.log('✅ TEACHER DASHBOARD - refreshQuranRecords - Stats rafraîchies:', {
        ...statsData,
        attendanceResponseSuccess: attendanceResponse.success,
        attendanceResponseError: attendanceResponse.error,
        attendanceData: attendanceResponse.data,
        presentTodayValue: presentTodayValue,
        absentTodayValue: absentTodayValue
      });
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des statistiques:', error);
      // En cas d'erreur, calculer localement en préservant les valeurs d'attendance
      const progressDistribution = calculateProgressDistribution();
      setStats(prevStats => ({
        ...prevStats,
        totalStudents: students.length,
        progressDistribution,
        presentToday: prevStats?.presentToday || 0,
        absentToday: prevStats?.absentToday || 0
      }));
      console.log('✅ TEACHER DASHBOARD - Statistiques calculées localement (fallback)', {
        progressDistribution,
        totalStudents: students.length
      });
    } finally {
      setRefreshing(null);
    }
  }, [apiService, calculateProgressDistribution, students, timezone]);

  // Fonction pour rafraîchir les messages
  const refreshMessages = async () => {
    setRefreshing('messages');
    try {
      const messagesResponse = await apiService.getTeacherMessages();
      if (messagesResponse.success) {
        // Fonction helper pour extraire le nom de l'étudiant depuis le contenu
        const extractStudentName = (content: string): { studentName: string | null; cleanContent: string } => {
          if (!content) return { studentName: null, cleanContent: content || '' };
          
          let cleanContent = GLOBAL_UTILS.cleanText(content);
          let studentName = null;
          
          // Chercher le pattern avec les différentes formes inclusives
          const studentLabelPatterns = [
            /الطالب\/ـة:\s*([^\n]+)/i,       // Arabe: الطالب/ـة
            /الطالب\/الطالبة:\s*([^\n]+)/i,  // Arabe: الطالب/الطالبة (ancien format)
            /الطالب\(ة\):\s*([^\n]+)/i,      // Arabe: الطالب(ة)
            /الطالب:\s*([^\n]+)/i,           // Arabe: الطالب (ancien format)
            /Student\(s\):\s*([^\n]+)/i,     // Anglais: Student(s)
            /Student:\s*([^\n]+)/i,          // Anglais: Student (ancien format)
            /Étudiant\(e\):\s*([^\n]+)/i,    // Français: Étudiant(e)
            /Étudiant:\s*([^\n]+)/i          // Français: Étudiant (ancien format)
          ];
          
          for (const pattern of studentLabelPatterns) {
            const match = cleanContent.match(pattern);
            if (match && match[1]) {
              studentName = match[1].trim();
              // Retirer le label et le nom de l'étudiant du contenu pour l'affichage
              // Le label correct sera affiché séparément avec GLOBAL_UTILS.getStudentLabel()
              cleanContent = cleanContent.replace(pattern, '').trim();
              // Retirer les sauts de ligne multiples au début
              cleanContent = cleanContent.replace(/^\n+/, '').trim();
              break;
            }
          }
          
          return { studentName, cleanContent };
        };
        
        const mappedMessages = (messagesResponse.data || []).map((message: any) => {
          const { studentName, cleanContent } = extractStudentName(message.content || '');
          
          // Utiliser student_id pour trouver le nom de l'étudiant dans la liste des étudiants
          let finalStudentName = null;
          if (message.student_id) {
            const student = students.find(s => s.id === message.student_id);
            finalStudentName = student ? student.student_name : null;
          }
          
          // Si pas trouvé via student_id, utiliser student_name de l'API
          if (!finalStudentName && message.student_name) {
            finalStudentName = message.student_name;
          }
          
          // Sinon, utiliser le nom extrait depuis le contenu
          if (!finalStudentName) {
            finalStudentName = studentName;
          }
          
          return {
            ...message,
            id: message.id,
            from: message.sender_name || message.from,
            subject: message.title || message.subject,
            date: message.created_date || message.date,
            content: cleanContent,
            isRead: false,
            student_id: message.student_id || null,
            // Utiliser le nom trouvé via student_id en priorité, sinon depuis l'API, sinon depuis le contenu
            student_name: finalStudentName,
            // Mapper reply_to_message_id depuis l'API
            is_reply: message.reply_to_message_id !== null && message.reply_to_message_id !== undefined,
            reply_to_message_id: message.reply_to_message_id || null
          };
        });
        // Dédupliquer les messages par ID
        const uniqueMessages = deduplicateMessages(mappedMessages);
        setMessages(uniqueMessages);
        console.log('✅ TEACHER DASHBOARD - Messages rafraîchis:', uniqueMessages.length, '(dédupliqués de', mappedMessages.length, ')');
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des messages:', error);
    } finally {
      setRefreshing(null);
    }
  };

  const refreshQuranRecords = React.useCallback(async () => {
    setRefreshing('quran');
    try {
      console.log('🔄 TEACHER DASHBOARD - refreshQuranRecords - FORCE REFRESH');
      
      // 1. Récupérer les enregistrements Coran (sans les données ns_*)
      const quranResponse = await apiService.getTeacherQuranRecords(true);
      if (!quranResponse.success) {
        alert(t('errorUpdatingData') + ': ' + quranResponse.error);
        return false;
      }

      // 2. Récupérer toutes les sessions suivantes pour cet enseignant
      const currentTeacher = apiService.getCurrentUser();
      const teacherId = currentTeacher?.id || currentTeacher?.user_id || currentTeacher?.teacherId;
      
      let nextSessionsMap = new Map();
      if (teacherId) {
        try {
          const nextSessionsResponse = await apiService.getNextSessionsByTeacher(teacherId);
          if (nextSessionsResponse.success && nextSessionsResponse.data) {
            // Créer un Map pour un accès rapide (studentId -> nextSession)
            nextSessionsResponse.data.forEach((nextSession: any) => {
              const studentId = nextSession.studentId || nextSession.student_id;
              if (studentId) {
                nextSessionsMap.set(studentId, nextSession);
              }
            });
            console.log('✅ TEACHER DASHBOARD - Sessions suivantes récupérées:', nextSessionsMap.size);
          }
        } catch (nextSessionError) {
          console.warn('⚠️ TEACHER DASHBOARD - Erreur lors de la récupération des sessions suivantes:', nextSessionError);
          // Ne pas faire échouer toute l'opération si les sessions suivantes ne peuvent pas être récupérées
        }
      }

      // 3. Combiner les données : mapper les enregistrements Coran avec leurs sessions suivantes
      const mappedRecords = (quranResponse.data || []).map((record: any) => {
        const studentId = record.student_id || record.studentId;
        const nextSession = nextSessionsMap.get(studentId) || null;
        
        
        // Utiliser la fonction de mapping centralisée
        return mapQuranRecordFromBackend(record, nextSession);
      });
      
      // Forcer la mise à jour en créant un nouveau tableau (évite les problèmes de référence)
      setQuranRecords([...mappedRecords]);
      console.log('✅ TEACHER DASHBOARD - Données rafraîchies, nombre d\'enregistrements:', mappedRecords.length);
      return true;
    } catch (error) {
      console.error('❌ TEACHER DASHBOARD - Erreur lors du rafraîchissement:', error);
      alert(t('errorUpdatingData'));
      return false;
    } finally {
      setRefreshing(null);
    }
  }, [apiService, t]);

  // Fonction pour générer automatiquement le texte de الملاحظة basé sur les évaluations

  // Caractères spéciaux invisibles pour délimiter le texte d'évaluation
  // NOTE: Toutes les fonctions liées à "متابعة القرآن" (extractOriginalNoteText, wrapEvaluationText, 
  // handleNoteChange, openAddQuranRecordModal, closeAddQuranRecordModal, handleAddQuranRecord) 
  // ont été déplacées dans QuranTrackingSection.tsx

  // Fonction pour supprimer un enregistrement Coran (non utilisée actuellement)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteQuranRecord = async (recordId: number) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        const response = await apiService.deleteQuranRecord(recordId);
        if (response.success) {
        // Rafraîchir les enregistrements Coran et les statistiques
          await refreshQuranRecords();
        await refreshStats();
        } else {
        alert(t('errorDeletingRecord') + ': ' + response.error);
        }
      } catch (error) {
        alert(t('errorDeletingRecord'));
      }
    }
  };

  const handleSendQuickReport = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!quickReport.sendToAdmin) {
      // Si "الإدارة" n'est pas cochée, l'étudiant est obligatoire
      if (!quickReport.studentId) {
        alert(t('selectStudentOrAdmin'));
        return;
      }
    }
    
    // Si "الإدارة" est cochée, l'étudiant est optionnel (on peut envoyer uniquement à la direction)
    
    // Log de début d'envoi (sera mis à jour après la recherche des parents)
    const sendLogStart = {
      action: 'send_quick_report',
      component: 'TeacherDashboard',
      timestamp: new Date().toISOString(),
      route: '/api/teacher/quick-report',
      method: 'POST',
      report_data: {
        sendToAdmin: quickReport.sendToAdmin,
        studentId: quickReport.studentId,
        reportType: quickReport.reportType,
        message: quickReport.message?.substring(0, 100) + (quickReport.message?.length > 100 ? '...' : ''),
        message_length: quickReport.message?.length || 0
      }
    };
    console.log('📤 TEACHER DASHBOARD - handleSendQuickReport - Début:', JSON.stringify(sendLogStart, null, 2));
    
    try {
      // Déterminer recipient_type selon les sélections
      let recipientType: 'admin' | 'specific';
      
      if (quickReport.sendToAdmin) {
        // Si "الإدارة" est cochée, recipient_type = 'admin'
        // Le backend gère automatiquement la récupération des parents si studentId est fourni
        recipientType = 'admin';
      } else {
        // Si "الإدارة" n'est pas cochée, recipient_type = 'specific'
        // Le backend trouve automatiquement le parent via student_id
        recipientType = 'specific';
        
        if (!quickReport.studentId) {
          alert(t('selectStudentRequired'));
          return;
        }
      }
      
      // Déterminer le type de rapport si c'est une réponse
      let reportTypeToSend = quickReport.reportType;
      if (showReplyMessageModal && messageToReplyTo && messageToReplyTo.subject) {
        // Si c'est une réponse, extraire le type de rapport du sujet original
        const originalSubject = messageToReplyTo.subject.toLowerCase();
        if (originalSubject.includes('assignment') || originalSubject.includes('واجب') || originalSubject.includes('devoir')) {
          reportTypeToSend = 'assignment';
        } else if (originalSubject.includes('progress') || originalSubject.includes('تقدم') || originalSubject.includes('progrès')) {
          reportTypeToSend = 'progress';
        } else if (originalSubject.includes('achievement') || originalSubject.includes('إنجاز') || originalSubject.includes('réussite')) {
          reportTypeToSend = 'achievement';
        } else if (originalSubject.includes('concern') || originalSubject.includes('ملاحظة') || originalSubject.includes('note importante')) {
          reportTypeToSend = 'concern';
        }
      }
      
      // Préparer les données pour l'envoi
      // Le backend gère automatiquement la récupération du parent via student_id
      const reportData: any = {
        studentId: quickReport.studentId || 0, // Peut être 0 si on envoie uniquement à la direction
        recipient_type: recipientType,
        reportType: reportTypeToSend || quickReport.reportType,
        message: quickReport.message,
        priority: 'normal',
        subject: quickReport.subject || '' // Subject de la réponse
      };
      
      // Si c'est une réponse, ajouter les informations de réponse
      if (showReplyMessageModal && messageToReplyTo) {
        reportData.is_reply = true;
        reportData.reply_to_message_id = messageToReplyTo.id;
      }
      
      // Pour recipient_type='admin' avec studentId, le backend enverra aux admins + parent de l'étudiant
      // Pour recipient_type='admin' sans studentId, le backend enverra uniquement aux admins
      // Pour recipient_type='specific', le backend trouve automatiquement le parent via student_id
      // On n'a pas besoin de chercher les parents, le backend le fait automatiquement
      
      const response = await apiService.sendQuickReport(reportData);
      
      // Log après réponse
      const sendLogAfter = {
        action: 'send_quick_report',
        component: 'TeacherDashboard',
        timestamp: new Date().toISOString(),
        response: {
          success: response.success,
          message_id: response.data?.id || null,
          recipients_count: response.data?.recipients_count || null,
          error: response.error || null
        }
      };
      console.log('📤 TEACHER DASHBOARD - handleSendQuickReport - Après réponse:', JSON.stringify(sendLogAfter, null, 2));
      
      if (response.success) {
        alert(t('reportSent'));
        // Fermer la modal appropriée
        if (showReplyMessageModal) {
          closeReplyMessageModal();
        } else {
          setShowQuickReportModal(false);
        }
        setQuickReport({
          studentId: '',
          reportType: '',
          message: '',
          sendToAdmin: false,
          subject: ''
        });
        // Utiliser refreshMessages pour éviter la duplication
        await refreshMessages();
      } else {
        alert(t('errorSendingReport') + ': ' + response.error);
      }
    } catch (error: any) {
      const sendLogError = {
        action: 'send_quick_report',
        component: 'TeacherDashboard',
        timestamp: new Date().toISOString(),
        exception: {
          message: error?.message || 'Erreur inconnue',
          type: error?.constructor?.name || 'Error'
        }
      };
      console.error('❌ TEACHER DASHBOARD - handleSendQuickReport - Erreur:', JSON.stringify(sendLogError, null, 2));
      alert(t('errorSendingReport'));
    }
  };

  // Fonction pour répondre à un message
  const handleReplyToMessage = (message: Message) => {
    setMessageToReplyTo(message);
    setShowReplyMessageModal(true);
    
    // Déterminer le destinataire selon le type d'expéditeur
    const senderType = message.sender_type?.toLowerCase() || '';
    let sendToAdmin = false;
    
    if (senderType === 'admin' || senderType === 'administrator' || senderType === 'مدير') {
      sendToAdmin = true;
    } else if (senderType === 'parent' || senderType === 'ولي الأمر') {
      // Si c'est un parent, on doit envoyer au parent spécifique (via student_id)
      sendToAdmin = false;
    } else {
      // Par défaut, envoyer à l'administration si on ne peut pas déterminer
      sendToAdmin = true;
    }
    
    // Préfixer le sujet avec un symbole de réponse (↩) SANS traduction
    // Le subject doit être enregistré tel quel dans la base de données
    let replySubject = message.subject || '';
    // Utiliser le symbole ↩ au lieu de "رد:"
    if (!replySubject.toLowerCase().startsWith('re:') && !replySubject.toLowerCase().startsWith('↩') && !replySubject.startsWith('↩')) {
      replySubject = `↩ ${replySubject}`; // Préfixer sans traduction
    } else {
      // Si déjà préfixé, garder tel quel
      replySubject = replySubject.replace(/^(رد:|re:)\s*/i, '↩ ');
    }
    
    // Construire le message avec les informations du message original formatées (style Gmail)
    // Format: 3 lignes vides + informations avec préfixes "> " + contenu du message
    const studentLabel = message.student_label || 
      GLOBAL_UTILS.getStudentLabel(message.student_gender) ||
      (message.student_id ? GLOBAL_UTILS.getStudentLabel(students.find(s => s.id === message.student_id)?.gender) : 'الطالب/ـة');
    
    // Construire les lignes du message formaté
    const messageLines: string[] = [
      '', // Ligne vide 1
      '', // Ligne vide 2
      '', // Ligne vide 3
      `> ${t('from')}: ${message.from || message.sender_name || ''}${message.sender_type ? ` (${getSenderTitle(message.sender_type)})` : ''}`
    ];
    
    // Ajouter l'étudiant si disponible
    if (message.student_name) {
      messageLines.push(`> ${studentLabel}: ${message.student_name}`);
    }
    
    // Ajouter le sujet (sans traduction - tel quel)
    messageLines.push(`> ${t('messageSubject')}: ${message.subject || ''}`);
    
    // Ajouter la date
    const messageDate = message.date || message.created_date || '';
    const messageTime = message.sent_time || '';
    messageLines.push(`> ${t('date')}: ${messageDate ? GLOBAL_UTILS.formatDate(messageDate) : ''}${messageTime ? ` ${messageTime}` : ''}`);
    
    // Ligne vide avant le contenu
    messageLines.push('>');
    
    // Ajouter le contenu du message avec préfixes "> "
    const content = message.content || '';
    const contentLines = content.split('\n').map((line) => `> ${line}`);
    messageLines.push(...contentLines);
    
    const formattedOriginalMessage = messageLines.join('\n');
    
    // Extraire le type de rapport du sujet original
    let reportType = '';
    if (message.subject) {
      const originalSubject = message.subject.toLowerCase();
      if (originalSubject.includes('assignment') || originalSubject.includes('واجب') || originalSubject.includes('devoir')) {
        reportType = 'assignment';
      } else if (originalSubject.includes('progress') || originalSubject.includes('تقدم') || originalSubject.includes('progrès')) {
        reportType = 'progress';
      } else if (originalSubject.includes('achievement') || originalSubject.includes('إنجاز') || originalSubject.includes('réussite')) {
        reportType = 'achievement';
      } else if (originalSubject.includes('concern') || originalSubject.includes('ملاحظة') || originalSubject.includes('note importante')) {
        reportType = 'concern';
      }
    }
    
    setQuickReport({
      studentId: message.student_id ? String(message.student_id) : '',
      reportType: reportType,
      message: formattedOriginalMessage,
      sendToAdmin: sendToAdmin,
      subject: replySubject
    });
  };

  // Fonction pour fermer la modal de réponse
  const closeReplyMessageModal = () => {
    setShowReplyMessageModal(false);
    setMessageToReplyTo(null);
    setQuickReport({
      studentId: '',
      reportType: '',
      message: '',
      sendToAdmin: false,
      subject: ''
    });
  };

  const closeQuickReportModal = () => {
    setShowQuickReportModal(false);
    setQuickReport({
      studentId: '',
      reportType: '',
      message: '',
      sendToAdmin: false,
      subject: ''
    });
  };

  // Fonction pour gérer le clic sur un message (expansion/réduction)
  const handleMessageClick = (messageId: number, e?: React.MouseEvent) => {
    // Empêcher la propagation si on clique sur le bouton de réponse
    if (e && (e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (expandedMessageId === messageId) {
      // Si on clique sur le même message, le fermer
      setExpandedMessageId(null);
    } else {
      // Sinon, ouvrir le nouveau message (ferme automatiquement l'ancien)
      setExpandedMessageId(messageId);
    }
  };

  // Fonction pour extraire le texte sans HTML (style AdminDashboard)
  const getPlainText = (content: string | null | undefined, maxLength: number = 80): string => {
    if (!content) return 'لا يوجد محتوى';
    
    // Supprimer tous les préfixes "> " ou "< " (y compris multiples comme ">>") de chaque ligne
    let text = content.split('\n').map(line => {
      // Supprimer tous les ">" et espaces au début de la ligne
      let cleaned = line.replace(/^[>\s]+/, '');
      // Supprimer aussi les "<" et espaces au début
      cleaned = cleaned.replace(/^[<\s]+/, '');
      return cleaned.trim();
    }).join(' ').trim();
    
    // Nettoyer les espaces multiples
    text = text.replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Fonction pour garder le contenu tel quel avec les préfixes et les retours à la ligne (pour l'affichage complet)
  const getCleanedContent = (content: string | null | undefined): string => {
    if (!content) return 'لا يوجد محتوى';
    
    // Normaliser les retours à la ligne (Windows \r\n -> \n, Mac \r -> \n) pour garantir un affichage correct
    // Préserver tous les préfixes "> ", ">>", "> >", etc. et les retours à la ligne
    return content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  };

  // Utilisation des styles globaux
  const styles = GLOBAL_STYLES.dashboard;

  // Callbacks mémorisés pour QuranTrackingSection pour éviter les re-renders inutiles
  // IMPORTANT: Ces hooks doivent être définis AVANT tout return conditionnel pour respecter les règles des hooks
  const handleGetStudents = React.useCallback(async () => {
    // Forcer le rechargement complet sans cache
    return await apiService.getTeacherStudents(true);
  }, [apiService]);
  const handleGetQuranRecordById = React.useCallback(async (id: number) => await apiService.getQuranRecordById(id), [apiService]);
  const handleAddQuranRecord = React.useCallback(async (record: any) => await apiService.addQuranRecord(record), [apiService]);
  const handleUpdateQuranRecord = React.useCallback(async (id: number, record: any) => await apiService.updateQuranRecord(id, record), [apiService]);
  const handleUpdateRepeatNewHifz = React.useCallback(async (recordId: number, repeatNewHifz: number) => await apiService.updateRepeatNewHifz(recordId, repeatNewHifz), [apiService]);
  const handleCreateOrUpdateNextSession = React.useCallback(async (studentId: number, sessionData: any) => {
    return await apiService.createOrUpdateNextSession(studentId, sessionData);
  }, [apiService]);

  // Callback pour récupérer la session suivante d'un étudiant
  const handleGetNextSessionForStudent = React.useCallback(async (studentId: number) => {
    return await apiService.getNextSessionForStudent(studentId);
  }, [apiService]);
  const handleRecordAdded = React.useCallback(async () => {
    await refreshQuranRecords();
    await refreshStats();
  }, [refreshQuranRecords, refreshStats]);
  const handleRecordUpdated = React.useCallback(async () => {
    await refreshQuranRecords();
    await refreshStats();
  }, [refreshQuranRecords, refreshStats]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '128px',
              height: '128px',
              border: '2px solid #9333ea',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ marginTop: '16px', color: '#6b7280' }}>جاري التحميل...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ef4444', fontSize: '4rem', marginBottom: '16px' }}>❌</div>
            <p style={{ color: '#dc2626', fontSize: '1.25rem' }}>{error}</p>
            <button 
              onClick={onBackHome}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                marginTop: '16px'
              }}
            >
              {t('backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      {/* Navigation */}
      <nav style={{
        ...styles.nav,
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #e0e0e0',
        marginBottom: '30px',
        padding: 0
      }}>
        <div style={{
          ...styles.navContent,
          maxWidth: '1200px',
          padding: windowWidth < 768 ? '0 15px' : '0 30px',
          height: windowWidth < 768 ? 'auto' : '70px',
          minHeight: windowWidth < 768 ? '60px' : '70px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          columnGap: windowWidth < 768 ? '12px' : '24px',
          flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <div style={{
              width: windowWidth < 768 ? '50px' : '65px',
              height: windowWidth < 768 ? '50px' : '65px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}>
              <img 
                src={logoUrl}
                alt={getName(currentLanguage)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
              />
            </div>
            {windowWidth >= 768 && (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '400', lineHeight: '1.4' }}>{t('associationName')}</div>
                <div style={{ fontSize: '0.9rem', color: '#757575', fontWeight: '300', lineHeight: '1.4' }}>{t('teacherBoardSubtitle')}</div>
              </div>
            )}
          </div>
          {windowWidth < 768 ? (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '20px',
                marginLeft: 'auto'
              }}
            >
              ☰
            </button>
          ) : (
            <div style={{ 
              display: 'flex',
              gap: '2px',
              flexWrap: 'wrap' as const,
              alignItems: 'center',
              alignSelf: 'center'
            }}>
            <button 
              onClick={() => showTeacherSection('teacherHome')} 
              style={{
                ...styles.navButton,
                padding: '12px 24px',
                fontSize: '18px',
                borderRadius: '6px',
                ...getNavButtonStyle('teacherHome', activeSection === 'teacherHome')
              }}
            >
              {t('teacherHome')}
            </button>
            <button 
              onClick={() => showTeacherSection('quranTracking')} 
              style={{
                ...styles.navButton,
                padding: '12px 24px',
                fontSize: '18px',
                borderRadius: '6px',
                ...getNavButtonStyle('quranTracking', activeSection === 'quranTracking')
              }}
            >
              {t('quranTracking')}
            </button>
            <button 
              onClick={() => showTeacherSection('studentProgress')} 
              style={{
                ...styles.navButton,
                padding: '12px 24px',
                fontSize: '18px',
                borderRadius: '6px',
                ...getNavButtonStyle('studentProgress', activeSection === 'studentProgress')
              }}
            >
              {t('studentProgress')}
            </button>
            <button 
              onClick={() => showTeacherSection('teacherMessages')} 
              style={{
                ...styles.navButton,
                padding: '12px 24px',
                fontSize: '18px',
                borderRadius: '6px',
                ...getNavButtonStyle('teacherMessages', activeSection === 'teacherMessages')
              }}
            >
              {t('teacherMessages')}
            </button>
            <button 
              onClick={handleLogout}
              style={{
                ...styles.navButton,
                padding: '12px 24px',
                fontSize: '18px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: '#d32f2f',
                border: '1px solid #ffcdd2'
              }}
            >
              {t('logout')}
            </button>
          </div>
          )}
        </div>
        {/* Menu mobile */}
        {windowWidth < 768 && isMobileMenuOpen && (
          <div style={{
            width: '100%',
            padding: '15px',
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <button 
              onClick={() => { showTeacherSection('teacherHome'); setIsMobileMenuOpen(false); }} 
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                width: '100%',
                textAlign: currentLanguage === 'ar' ? 'right' : 'left',
                ...getNavButtonStyle('teacherHome', activeSection === 'teacherHome')
              }}
            >
              {t('teacherHome')}
            </button>
            <button 
              onClick={() => { showTeacherSection('quranTracking'); setIsMobileMenuOpen(false); }} 
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                width: '100%',
                textAlign: currentLanguage === 'ar' ? 'right' : 'left',
                ...getNavButtonStyle('quranTracking', activeSection === 'quranTracking')
              }}
            >
              {t('quranTracking')}
            </button>
            <button 
              onClick={() => { showTeacherSection('studentProgress'); setIsMobileMenuOpen(false); }} 
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                width: '100%',
                textAlign: currentLanguage === 'ar' ? 'right' : 'left',
                ...getNavButtonStyle('studentProgress', activeSection === 'studentProgress')
              }}
            >
              {t('studentProgress')}
            </button>
            <button 
              onClick={() => { showTeacherSection('teacherMessages'); setIsMobileMenuOpen(false); }} 
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                width: '100%',
                textAlign: currentLanguage === 'ar' ? 'right' : 'left',
                ...getNavButtonStyle('teacherMessages', activeSection === 'teacherMessages')
              }}
            >
              {t('teacherMessages')}
            </button>
            <button 
              onClick={handleLogout}
              style={{
                ...styles.navButton,
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '6px',
                width: '100%',
                textAlign: currentLanguage === 'ar' ? 'right' : 'left',
                backgroundColor: 'transparent',
                color: '#d32f2f',
                border: '1px solid #ffcdd2'
              }}
            >
              {t('logout')}
            </button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div style={{
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: windowWidth < 768 ? '16px 15px' : windowWidth < 1024 ? '24px 20px' : '32px 30px',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
        {/* Teacher Home Section */}
        {activeSection === 'teacherHome' && (
          <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: windowWidth < 768 ? '16px' : '24px',
              width: '100%',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h1 style={{ fontSize: windowWidth < 768 ? '1.5rem' : '1.875rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                {t('teacherHomePage')}
              </h1>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                  }}
                >
                  🔒 {t('changePassword')}
                </button>
              <button
                onClick={refreshStats}
                disabled={refreshing === 'stats'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: refreshing === 'stats' ? '#9ca3af' : '#9333ea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: refreshing === 'stats' ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (refreshing !== 'stats') {
                    e.currentTarget.style.backgroundColor = '#7e22ce';
                  }
                }}
                onMouseOut={(e) => {
                  if (refreshing !== 'stats') {
                    e.currentTarget.style.backgroundColor = '#9333ea';
                  }
                }}
              >
                <RefreshIcon animate={refreshing === 'stats'} />
                  <span>{t('update')}</span>
              </button>
              </div>
            </div>
            <div style={styles.card}>
              <div style={styles.welcomeCard}>
                <div>
                  <h1 style={styles.welcomeTitle}>
                    {currentTeacher?.gender === 'Female' ? t('welcomeTeacherFemale') : t('welcomeTeacherMale')} {currentTeacher?.full_name || currentTeacher?.fullName || ''}
                  </h1>
                  <p style={styles.welcomeSubtitle}>{t('quranTrackingSubtitle')}</p>
                </div>
              </div>
            </div>
            
              <div style={{
              display: 'grid',
              gridTemplateColumns: windowWidth < 768 ? 'repeat(2, 1fr)' : windowWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: windowWidth < 768 ? '12px' : '20px',
              marginBottom: windowWidth < 768 ? '24px' : '40px',
              width: '100%'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #9333ea'
              }}>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '300', 
                  color: '#9333ea',
                  marginBottom: '12px'
                }}>
                  {stats?.totalStudents || 0}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: '16px',
                  fontWeight: '400'
                }}>{t('totalStudents')}</div>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #10b981'
              }}>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '300', 
                  color: '#10b981',
                  marginBottom: '12px'
                }}>
                  {stats?.averageMemorization || 0}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: '16px',
                  fontWeight: '400'
                }}>{t('averageHifz')}</div>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #3b82f6'
              }}>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '300', 
                  color: '#3b82f6',
                  marginBottom: '12px'
                }}>
                  {stats?.presentToday || 0}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: '16px',
                  fontWeight: '400'
                }}>{t('presentToday')}</div>
                {!todayHasSession && (
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>{t('noSessionsToday')}</div>
                )}
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                borderRight: '4px solid #f59e0b'
              }}>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '300', 
                  color: '#f59e0b',
                  marginBottom: '12px'
                }}>
                  {stats?.absentToday || 0}
                </div>
                <div style={{ 
                  color: '#424242', 
                  fontSize: '16px',
                  fontWeight: '400'
                }}>{t('absentToday')}</div>
                {!todayHasSession && (
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>{t('noSessionsToday')}</div>
                )}
              </div>
            </div>

            {/* Recent Activities */}
            <div style={styles.card}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                {t('recentMessages')}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {stats?.recentActivities?.map((activity, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#f0fdf4',
                    borderRadius: '8px'
                  }}>
                    <div style={{ color: '#10b981', marginLeft: '12px' }}>📖</div>
                    <div>
                      <p style={{ fontWeight: '500', margin: 0 }}>
                        {activity.studentName} - {activity.activity}
                      </p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        {activity.type}
                      </p>
                    </div>
                    <div style={{ marginRight: 'auto', fontSize: '14px', color: '#6b7280' }}>
                      {activity.date}
                    </div>
                  </div>
                )) || (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                    {t('noMessages')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quran Tracking Section */}
        {activeSection === 'quranTracking' && (
          <QuranTrackingSection
            hideTitle={false}
            hideHeader={false}
            students={students}
            quranRecords={quranRecords}
            onRefreshQuranRecords={refreshQuranRecords}
            onRefreshStats={refreshStats}
            onGetStudents={handleGetStudents}
            onGetQuranRecordById={handleGetQuranRecordById}
            onAddQuranRecord={handleAddQuranRecord}
            onUpdateQuranRecord={handleUpdateQuranRecord}
            onUpdateRepeatNewHifz={handleUpdateRepeatNewHifz}
            onCreateOrUpdateNextSession={handleCreateOrUpdateNextSession}
            onGetNextSessionForStudent={handleGetNextSessionForStudent}
            currentTeacherName={currentTeacher?.full_name || currentTeacher?.fullName || currentTeacher?.username || ''}
            t={t}
            currentLanguage={currentLanguage}
            windowWidth={windowWidth}
            styles={styles}
            canModify={true}
            onRecordAdded={handleRecordAdded}
            onRecordUpdated={handleRecordUpdated}
          />
        )}

        {/* ========== Student Progress Section (تقدم الطلاب) ========== */}
        {activeSection === 'studentProgress' && (
          <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
            {/* En-tête */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: windowWidth < 768 ? '20px' : '32px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h1 style={{
                fontSize: windowWidth < 768 ? '1.5rem' : '1.875rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: 0
              }}>
                {t('studentProgressTitle')}
              </h1>
            </div>

            {/* Onglets */}
            <div style={{
              display: 'flex',
              gap: '0',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '20px'
            }}>
              <button
                onClick={() => setStudentProgressTab('examScores')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: studentProgressTab === 'examScores' ? '#FFE0B2' : 'transparent',
                  color: studentProgressTab === 'examScores' ? '#E65100' : '#666666',
                  border: 'none',
                  borderBottom: studentProgressTab === 'examScores' ? '3px solid #FF6F00' : 'none',
                  cursor: 'pointer',
                  fontWeight: studentProgressTab === 'examScores' ? '600' : '400',
                  fontSize: '15px',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
              >
                {t('examScoresTab')}
              </button>
              
              <button
                onClick={() => setStudentProgressTab('quranStats')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: studentProgressTab === 'quranStats' ? '#C5E1A5' : 'transparent',
                  color: studentProgressTab === 'quranStats' ? '#33691E' : '#666666',
                  border: 'none',
                  borderBottom: studentProgressTab === 'quranStats' ? '3px solid #689F38' : 'none',
                  cursor: 'pointer',
                  fontWeight: studentProgressTab === 'quranStats' ? '600' : '400',
                  fontSize: '15px',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
              >
                {t('memorizationStats')}
              </button>
            </div>

            {/* Contenu selon l'onglet sélectionné */}
            {studentProgressTab === 'examScores' ? (
              // ===== ONGLET: نقط الإختبارات =====
              <div>
                {/* Bouton d'ajout (conditionnel) */}
                {allowTeacherToAddExamPoints && (
                  <div style={{ marginBottom: '20px' }}>
                    <button
                      style={{
                        backgroundColor: '#9c27b0',
                        color: 'white',
                        padding: windowWidth < 768 ? '10px 14px' : '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        fontSize: windowWidth < 768 ? '13px' : '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(156, 39, 176, 0.2)',
                      }}
                      onClick={async () => {
                        // Recharger les matières avant d'ouvrir le formulaire
                        try {
                          const subjectsData = await apiService.getSubjects(subjectLocale);
                          if (subjectsData && subjectsData.success && Array.isArray(subjectsData.data)) {
                            setSubjects(subjectsData.data);
                          }
                        } catch (error) {
                          console.error('خطأ في تحميل المواد:', error);
                        }
                        setShowAddTestScoreForm(true);
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#7b1fa2';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#9c27b0';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      ➕ {t('addExamScore')}
                    </button>
                  </div>
                )}

                {/* Tableau des notes - Filtrer pour n'afficher que les notes des étudiants du professeur */}
                <ExamScoresTable
                  testScores={testScores.filter(grade => {
                    // Ne garder que les notes des étudiants qui sont dans la liste students du professeur
                    return students.some(student => student.id === grade.student_id);
                  })}
                  students={students}
                  subjects={subjects}
                  windowWidth={windowWidth}
                  isRTL={currentLanguage === 'ar'}
                  showActions={allowTeacherToAddExamPoints}
                  onDeleteTestScore={handleDeleteTestScore}
                  t={t}
                  formatDate={(date) => formatShortDateInTz(new Date(date), timezone, currentLanguage === 'ar' ? 'ar-MA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US')}
                />
              </div>
            ) : (
              // ===== ONGLET: إحصائيات الحفظ =====
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '24px' : '40px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0'
              }}>
                <h2 style={{ 
                  fontSize: windowWidth < 768 ? '1.125rem' : '1.25rem', 
                  fontWeight: '600', 
                  color: '#1f2937', 
                  marginBottom: windowWidth < 768 ? '20px' : '24px',
                  textAlign: 'center'
                }}>
                  📖 {t('memorizationStats')}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '16px' : '20px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: windowWidth < 768 ? '12px' : '16px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <span style={{ 
                      color: '#166534', 
                      fontSize: windowWidth < 768 ? '13px' : '15px',
                      fontWeight: '500'
                    }}>
                      {t('advancedStudents')}
                    </span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#10b981',
                      fontSize: windowWidth < 768 ? '18px' : '20px'
                    }}>
                      {stats?.progressDistribution?.advanced || 0}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: windowWidth < 768 ? '12px' : '16px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <span style={{ 
                      color: '#1e40af', 
                      fontSize: windowWidth < 768 ? '13px' : '15px',
                      fontWeight: '500'
                    }}>
                      {t('intermediateStudents')}
                    </span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#3b82f6',
                      fontSize: windowWidth < 768 ? '18px' : '20px'
                    }}>
                      {stats?.progressDistribution?.intermediate || 0}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: windowWidth < 768 ? '12px' : '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fde68a'
                  }}>
                    <span style={{ 
                      color: '#92400e', 
                      fontSize: windowWidth < 768 ? '13px' : '15px',
                      fontWeight: '500'
                    }}>
                      {t('beginnerStudents')}
                    </span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#f59e0b',
                      fontSize: windowWidth < 768 ? '18px' : '20px'
                    }}>
                      {stats?.progressDistribution?.beginner || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Teacher Messages Section */}
        {activeSection === 'teacherMessages' && (
          <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: windowWidth < 768 ? '16px' : '32px',
              width: '100%',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h1 style={{ fontSize: windowWidth < 768 ? '1.5rem' : '1.875rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              {t('messagesAndCommunication')}
            </h1>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={refreshMessages}
                  disabled={refreshing === 'messages'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: windowWidth < 768 ? '8px 12px' : '10px 16px',
                    backgroundColor: refreshing === 'messages' ? '#9ca3af' : '#9333ea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: refreshing === 'messages' ? 'not-allowed' : 'pointer',
                    fontSize: windowWidth < 768 ? '12px' : '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (refreshing !== 'messages') {
                      e.currentTarget.style.backgroundColor = '#7e22ce';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (refreshing !== 'messages') {
                      e.currentTarget.style.backgroundColor = '#9333ea';
                    }
                  }}
                >
                  <RefreshIcon animate={refreshing === 'messages'} />
                  <span>{t('update')}</span>
                </button>
                <button 
                  onClick={() => setShowQuickReportModal(true)}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    padding: windowWidth < 768 ? '10px 16px' : '12px 24px',
                    fontSize: windowWidth < 768 ? '14px' : '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {t('quickReport')}
                </button>
              </div>
            </div>
            
            {/* Onglets pour الرسائل المرسلة et الرسائل المستلمة */}
            <div style={{
              display: 'flex',
              gap: '0',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '20px'
            }}>
              <button
                onClick={() => setMessageTab('sent')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: messageTab === 'sent' ? '#FFE0B2' : 'transparent',
                  color: messageTab === 'sent' ? '#E65100' : '#666666',
                  border: 'none',
                  borderBottom: messageTab === 'sent' ? '3px solid #FFB74D' : '3px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (messageTab !== 'sent') {
                    e.currentTarget.style.backgroundColor = '#FFF3E0';
                  }
                }}
                onMouseOut={(e) => {
                  if (messageTab !== 'sent') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t('sentMessages')}
              </button>
              <button
                onClick={() => setMessageTab('received')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: messageTab === 'received' ? '#FFE0B2' : 'transparent',
                  color: messageTab === 'received' ? '#E65100' : '#666666',
                  border: 'none',
                  borderBottom: messageTab === 'received' ? '3px solid #FFB74D' : '3px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (messageTab !== 'received') {
                    e.currentTarget.style.backgroundColor = '#FFF3E0';
                  }
                }}
                onMouseOut={(e) => {
                  if (messageTab !== 'received') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t('receivedMessages')}
              </button>
            </div>

            {/* Section Messages Reçus */}
            {messageTab === 'received' && (
            <div style={{...styles.card, width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: isRTL ? '0' : '8px',
                  paddingLeft: isRTL ? '8px' : '0'
                }}>
                {messages.filter(msg => {
                  const senderType = msg.sender_type?.toLowerCase() || '';
                  return senderType !== 'teacher' && senderType !== 'أستاذ';
                }).length > 0 ? (
                  messages.filter(msg => {
                    const senderType = msg.sender_type?.toLowerCase() || '';
                    return senderType !== 'teacher' && senderType !== 'أستاذ';
                  }).map((message) => {
                    const isOpen = expandedMessageId === message.id;
                    
                    // Fonction pour obtenir le genre de l'étudiant
                    const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                      if (!studentId) return null;
                      const student = students.find(s => s.id === studentId);
                      return student?.gender || null;
                    };
                    
                    // Fonction pour formater la date et l'heure (combinées)
                    const formatDateTime = (date: string, time?: string): string => {
                      const formattedDate = date ? GLOBAL_UTILS.formatDate(date) : t('dateUnknown');
                      return formattedDate + (time ? ` ${time}` : '');
                    };
                    
                    return (
                      <MessageItem
                        key={message.id}
                        message={message as MessageItemData}
                        isOpen={isOpen}
                        onToggle={handleMessageClick}
                        useModernStyle={false}
                        windowWidth={windowWidth}
                        translateReportType={translateReportType}
                        translatePriority={translatePriority}
                        getPlainText={getPlainText}
                        getCleanedContent={getCleanedContent}
                        showSenderLabel={true}
                        senderLabel={t('from')}
                        getSenderTitle={getSenderTitle}
                        getStudentGender={getStudentGender}
                        getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                        formatDate={GLOBAL_UTILS.formatDate}
                        formatDateTime={formatDateTime}
                        showReplyButton={true}
                        onReply={(msg) => {
                          // Convertir MessageItemData en Message pour handleReplyToMessage
                          const messageForReply: Message = {
                            ...message,
                            sender_type: msg.sender_type ?? undefined
                          };
                          handleReplyToMessage(messageForReply);
                        }}
                        showReplyBadge={false}
                        t={t}
                      />
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                    <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noReceivedMessages')}</p>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Section Messages Envoyés */}
            {messageTab === 'sent' && (
              <div style={{...styles.card, width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
              <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: isRTL ? '0' : '8px',
                  paddingLeft: isRTL ? '8px' : '0'
                }}>
                {messages.filter(msg => {
                  const senderType = msg.sender_type?.toLowerCase() || '';
                  return senderType === 'teacher' || senderType === 'أستاذ';
                }).length > 0 ? (
                  messages.filter(msg => {
                    const senderType = msg.sender_type?.toLowerCase() || '';
                    return senderType === 'teacher' || senderType === 'أستاذ';
                  }).map((message) => {
                    const isOpen = expandedMessageId === message.id;
                    
                    // Fonction pour obtenir le genre de l'étudiant
                    const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                      if (!studentId) return null;
                      const student = students.find(s => s.id === studentId);
                      return student?.gender || null;
                    };
                    
                    // Fonction pour formater la date et l'heure (combinées)
                    const formatDateTime = (date: string, time?: string): string => {
                      const formattedDate = date ? GLOBAL_UTILS.formatDate(date) : t('dateUnknown');
                      return formattedDate + (time ? ` ${time}` : '');
                    };
                    
                    return (
                      <MessageItem
                        key={message.id}
                        message={message as MessageItemData}
                        isOpen={isOpen}
                        onToggle={handleMessageClick}
                        useModernStyle={true}
                        windowWidth={windowWidth}
                        translateReportType={(subject) => subject || ''}
                        translatePriority={translatePriority}
                        getPlainText={getPlainText}
                        getCleanedContent={getCleanedContent}
                        showSenderLabel={false}
                        senderLabel={t('to')}
                        getStudentGender={getStudentGender}
                        getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                        formatDate={GLOBAL_UTILS.formatDate}
                        formatDateTime={formatDateTime}
                        showReplyButton={false}
                        showReplyBadge={false}
                        t={t}
                      />
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                    <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noSentMessages')}</p>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Quick Report Modal */}
      {showQuickReportModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              {t('quickReport')}
            </h2>
            <form onSubmit={handleSendQuickReport} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Checkbox pour "الإدارة" - Toujours visible */}
              <div style={styles.formGroup}>
                <label 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    padding: '12px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    backgroundColor: quickReport.sendToAdmin ? '#ede9fe' : '#f9fafb',
                    border: quickReport.sendToAdmin ? '2px solid #7c3aed' : '1px solid #d1d5db',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!quickReport.sendToAdmin) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!quickReport.sendToAdmin) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={quickReport.sendToAdmin}
                    onChange={(e) => {
                      setQuickReport({
                        ...quickReport,
                        sendToAdmin: e.target.checked
                      });
                    }}
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      cursor: 'pointer',
                      accentColor: '#7c3aed'
                    }}
                  />
                  <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                    {t('administration')}
                  </span>
                  <span style={{ 
                    fontSize: '12px', 
                    padding: '4px 10px', 
                    borderRadius: '12px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    fontWeight: '500'
                  }}>
                    {t('allAdmins')}
                  </span>
                </label>
                {quickReport.sendToAdmin && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', paddingLeft: isRTL ? '0' : '32px', paddingRight: isRTL ? '32px' : '0' }}>
                    {quickReport.studentId ? t('reportSentToAdminsAndParent') : t('reportSentToAdmins')}
                  </div>
                )}
              </div>
              
              {/* Sélection de l'étudiant - Toujours visible, optionnel si "الإدارة" est cochée */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t('student')}
                  {!quickReport.sendToAdmin && <span style={{ color: '#dc2626' }}> *</span>}
                  {quickReport.sendToAdmin && <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}> {t('optional')}</span>}
                </label>
                <select 
                  value={quickReport.studentId}
                  onChange={(e) => setQuickReport({...quickReport, studentId: e.target.value})}
                  required={!quickReport.sendToAdmin}
                  style={styles.select}
                >
                  <option value="">{t('selectStudent')}</option>
                  {students.map(student => (
                      <option key={student.id} value={student.id}>
                      {student.studentName}
                      </option>
                  ))}
                </select>
                {!quickReport.sendToAdmin && quickReport.studentId && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {t('reportSentToStudentGuardians')}
                  </div>
                )}
                {quickReport.sendToAdmin && quickReport.studentId && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {t('reportSentToAdminsAndGuardians')}
                  </div>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('reportType')}</label>
                <select 
                  value={quickReport.reportType}
                  onChange={(e) => setQuickReport({...quickReport, reportType: e.target.value})}
                  required 
                  style={styles.select}
                >
                  <option value="">{t('selectReportType')}</option>
                  <option value="progress">{t('reportTypeProgress')}</option>
                  <option value="achievement">{t('reportTypeAchievement')}</option>
                  <option value="concern">{t('reportTypeConcern')}</option>
                  <option value="assignment">{t('reportTypeAssignment')}</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('reportMessage')}</label>
                <textarea 
                  value={quickReport.message}
                  onChange={(e) => setQuickReport({...quickReport, message: e.target.value})}
                  required 
                  rows={4}
                  style={styles.textarea}
                  placeholder={t('messagePlaceholder')}
                />
              </div>
              <div style={styles.buttonGroup}>
                <button 
                  type="button"
                  onClick={closeQuickReportModal}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reply Message Modal */}
      {showReplyMessageModal && messageToReplyTo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeReplyMessageModal();
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '20px',
              textAlign: currentLanguage === 'ar' ? 'right' : 'left'
            }}>
              {t('replyToMessage')}
            </h2>
            
            <form onSubmit={handleSendQuickReport} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Affichage de l'étudiant - En lecture seule (pas de combobox) */}
              {messageToReplyTo.student_id && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('selectStudent')}
                  </label>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    color: '#374151',
                    fontWeight: '500'
                  }}>
                    {messageToReplyTo.student_name || 
                     (students.find(s => s.id === messageToReplyTo.student_id)?.student_name) ||
                     `ID: ${messageToReplyTo.student_id}`
                    }
                  </div>
                </div>
              )}
              
              {/* Affichage du nom du parent et checkbox "الإدارة" - Sur la même ligne */}
              <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}>
                {/* Affichage du nom du parent (si c'est une réponse à un parent) - Pour information */}
                {messageToReplyTo && (messageToReplyTo.sender_type?.toLowerCase() === 'parent' || messageToReplyTo.sender_type?.toLowerCase() === 'ولي الأمر') ? (
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>
                      {t('senderParent')}
                    </label>
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#e0f2fe',
                      borderRadius: '6px',
                      border: '2px solid #0284c7',
                      fontSize: '14px',
                      color: '#374151',
                      fontWeight: '500',
                      marginTop: '4px'
                    }}>
                      {messageToReplyTo.sender_name || (messageToReplyTo.from ? messageToReplyTo.from : t('notSpecified'))}
                    </div>
                  </div>
                ) : null}
                
                {/* Checkbox pour "الإدارة" */}
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>
                    {t('administration')}
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      padding: '12px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: quickReport.sendToAdmin ? '#ede9fe' : '#f9fafb',
                      border: quickReport.sendToAdmin ? '2px solid #7c3aed' : '1px solid #d1d5db',
                      transition: 'all 0.2s',
                      marginTop: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (!quickReport.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!quickReport.sendToAdmin) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={quickReport.sendToAdmin}
                      onChange={(e) => {
                        setQuickReport({
                          ...quickReport,
                          sendToAdmin: e.target.checked
                        });
                      }}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        cursor: 'pointer',
                        accentColor: '#7c3aed'
                      }}
                    />
                    <span style={{ fontSize: '16px', color: '#374151', flex: 1, fontWeight: '600' }}>
                      {t('administration')}
                    </span>
                  </label>
                  {quickReport.sendToAdmin && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', paddingLeft: isRTL ? '0' : '32px', paddingRight: isRTL ? '32px' : '0' }}>
                      {quickReport.studentId ? t('reportSentToAdminsAndParent') : t('reportSentToAdmins')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Champ objet - Pré-rempli avec le sujet de la réponse */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('messageSubject')}</label>
                <input
                  type="text"
                  value={quickReport.subject || (messageToReplyTo.subject ? `↩ ${messageToReplyTo.subject}` : '')}
                  onChange={(e) => setQuickReport({...quickReport, subject: e.target.value})}
                  style={styles.input}
                  placeholder={t('messageSubject')}
                />
              </div>
              
              {/* Champ message - Pré-rempli avec les informations du message original formatées (style Gmail) */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('message')}</label>
                <textarea 
                  value={quickReport.message}
                  onChange={(e) => setQuickReport({...quickReport, message: e.target.value})}
                  required 
                  rows={15}
                  style={{
                    ...styles.textarea,
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                  placeholder={t('messagePlaceholder')}
                />
              </div>
              
              <div style={styles.buttonGroup}>
                <button 
                  type="button"
                  onClick={closeReplyMessageModal}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...styles.flexButton
                  }}
                >
                  {t('sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ NOUVEAU : Modal d'ajout de note d'examen */}
      {showAddTestScoreForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowAddTestScoreForm(false);
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {t('addExamScore')}
            </h2>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleAddTestScore();
            }}>
              {/* Étudiant */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px',
                  textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {t('student')} *
                </label>
                <SearchableSelect
                  value={newTestScore.student_id}
                  onChange={(value) => setNewTestScore({ ...newTestScore, student_id: value })}
                  options={students.map(student => ({
                    value: student.id,
                    label: (student.student_name || student.studentName || `${t('student')} ${student.id}`) as string
                  }))}
                  placeholder={t('selectStudent')}
                />
              </div>

              {/* Matière */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px',
                  textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {t('subjectLabel')} *
                </label>
                <select
                  value={newTestScore.subject_id}
                  onChange={(e) => setNewTestScore({ ...newTestScore, subject_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    direction: currentLanguage === 'ar' ? 'rtl' : 'ltr'
                  }}
                >
                  <option value="">{t('selectSubject')}</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Note */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px',
                  textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {t('gradeLabel')} *
                </label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.25"
                  value={newTestScore.grade}
                  onChange={(e) => setNewTestScore({ ...newTestScore, grade: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    direction: currentLanguage === 'ar' ? 'rtl' : 'ltr'
                  }}
                />
              </div>

              {/* Date */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px',
                  textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {t('examDate')} *
                </label>
                <input
                  type="date"
                  value={newTestScore.date}
                  onChange={(e) => setNewTestScore({ ...newTestScore, date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    direction: currentLanguage === 'ar' ? 'rtl' : 'ltr'
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTestScoreForm(false);
                    setNewTestScore({
                      student_id: '',
                      subject_id: '',
                      grade: '',
                      date: todayInTz(timezone)
                    });
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#9c27b0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onChangePassword={async (oldPassword, newPassword) => {
          const response = await apiService.changePassword({ oldPassword, newPassword });
          return response;
        }}
        t={t}
        styles={styles}
        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
      />

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        /* Scrollbar personnalisée pour les sections de messages uniquement */
        .messages-container::-webkit-scrollbar {
          width: 13px;
        }
        .messages-container::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 12px;
          border: 1px solid #e0e0e0;
        }
        .messages-container::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #9c27b0 0%, #7b1fa2 100%);
          border-radius: 12px;
          border: 1px solid #6a1b9a;
        }
        .messages-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #7b1fa2 0%, #6a1b9a 100%);
        }
        .messages-container::-webkit-scrollbar-thumb:active {
          background: linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%);
        }
        /* Flèches du scrollbar (boutons haut/bas) */
        .messages-container::-webkit-scrollbar-button {
          display: block;
          height: 12px;
          width: 12px;
          background: linear-gradient(180deg, #9c27b0 0%, #7b1fa2 100%);
        }
        .messages-container::-webkit-scrollbar-button:hover {
          background: linear-gradient(180deg, #7b1fa2 0%, #6a1b9a 100%);
        }
        .messages-container::-webkit-scrollbar-button:active {
          background: linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%);
        }
        /* Flèche vers le haut */
        .messages-container::-webkit-scrollbar-button:single-button:vertical:decrement {
          border-radius: 10px 10px 0 0;
        }
        /* Flèche vers le bas */
        .messages-container::-webkit-scrollbar-button:single-button:vertical:increment {
          border-radius: 0 0 10px 10px;
        }
        /* Pour Firefox */
        .messages-container {
          scrollbar-width: auto;
          scrollbar-color: #9c27b0 #f5f5f5;
        }
      `}</style>
    </div>
  );
};

export default TeacherDashboard;